import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { analyticsModuleAttributes } from '../../lib/analytics-content.mjs'
import { dateLabel, localDateKey, timeLabel } from '../../lib/surf-forecast.mjs'
import { PhotoReport, PhotoSection } from './surf-photo.styles'

const windLabels = { calm: 'Calm', light: 'Light', moderate: 'Moderate', strong: 'Strong' }

export default function SurfPhoto() {
  const [photo, setPhoto] = useState(null)
  const [status, setStatus] = useState('loading')
  useEffect(() => {
    let disposed = false
    let controller
    const load = async () => {
      controller?.abort()
      const requestController = new AbortController()
      controller = requestController
      const timeout = setTimeout(() => requestController.abort(), 10000)
      try {
        const response = await fetch('/api/surf-reports/latest', { signal: requestController.signal })
        if (!response.ok) throw new Error('Photo unavailable')
        const data = await response.json()
        if (!disposed && controller === requestController) { setPhoto(data.photo); setStatus(data.photo ? 'ready' : 'empty') }
      } catch {
        if (!disposed && controller === requestController) setStatus('error')
      } finally { clearTimeout(timeout) }
    }
    load()
    const visibleLoad = () => { if (document.visibilityState === 'visible') load() }
    const interval = setInterval(visibleLoad, 60000)
    document.addEventListener('visibilitychange', visibleLoad)
    return () => { disposed = true; controller?.abort(); clearInterval(interval); document.removeEventListener('visibilitychange', visibleLoad) }
  }, [])
  const seconds = photo ? Date.parse(photo.capturedAt || photo.submittedAt) / 1000 : null
  return <PhotoSection aria-labelledby="serangan-photo-heading" data-surf-photo {...analyticsModuleAttributes({ _key: 'surf-forecast:serangan-beach:staff-photo' }, 'surf_staff_photo')}>
    <h2 id="serangan-photo-heading">Latest Serangan surf photo</h2>
    <p>A beach photo and conditions reported by our local team.</p>
    {photo ? <PhotoReport>
      <Image src={photo.image.url} width={photo.image.width} height={photo.image.height} sizes="(max-width: 767px) calc(100vw - 7.2rem), 56rem" alt={`Surf at Serangan Beach, Lombok, ${photo.capturedAt ? 'photographed' : 'shared'} ${dateLabel(localDateKey(seconds))} at ${timeLabel(seconds)} WITA`} />
      <figcaption>
        <p className="report-source">Staff observation · Serangan Beach</p>
        <p className="report-time">{photo.capturedAt ? 'Taken' : 'Shared'} <time dateTime={photo.capturedAt || photo.submittedAt}>{dateLabel(localDateKey(seconds), { day: 'numeric', month: 'long', year: 'numeric' })}<br />{timeLabel(seconds)} WITA</time></p>
        {!photo.capturedAt && <p className="report-note">Photo capture time unconfirmed.</p>}
        <dl><div><dt>Reported wind</dt><dd>{windLabels[photo.reportedWind] || 'Not reported'}</dd></div><div><dt>Surfers in the water</dt><dd>{photo.reportedSurfers ?? 'Not reported'}</dd></div></dl>
        <p className="report-note">This photo shows the beach at the reported time. Exploring forecast hours does not change it.</p>
        {photo.captureTimeSource === 'caption_time_assumed_message_date' && <p className="report-note">Time supplied by staff; date based on when the photo was shared.</p>}
        {status === 'error' && <p className="report-note" role="status">Could not check for a newer photo. The last loaded report is shown.</p>}
      </figcaption>
    </PhotoReport> : <p role="status">{status === 'loading' ? 'Loading the latest photo…' : status === 'error' ? 'The latest photo is temporarily unavailable.' : 'No staff photo has been shared yet.'}</p>}
  </PhotoSection>
}
